import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { Mail, Phone, MapPin, Globe2, Award, Send, CheckCircle2 } from "lucide-react";

export default function PublicCompany() {
  const { slug } = useParams();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company_name: "", country: "", message: "", inquiry_type: "inquiry", product_id: "" });
  const [sent, setSent] = useState(false);

  useEffect(() => {
    api.get(`/public/companies/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/public/companies/${slug}/inquiry`, form);
      setSent(true);
      setForm({ name: "", email: "", phone: "", company_name: "", country: "", message: "", inquiry_type: "inquiry", product_id: "" });
    } catch {}
  };

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500">Company not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">{t("common.loading")}</div>;

  const { company, products, sales_reps } = data;
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-30 backdrop-blur-xl bg-white/85 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            {company.logo_url ? (
              <img src={`${BACKEND}${company.logo_url}`} alt={company.name} className="w-8 h-8 rounded object-cover" />
            ) : (
              <div className="w-8 h-8 rounded bg-[#0052CC] text-white flex items-center justify-center font-bold text-xs">{company.name[0]}</div>
            )}
            <span className="font-heading font-bold text-slate-900">{company.name}</span>
          </Link>
          <div className="flex items-center gap-3">
            <a href="#contact" className="text-sm font-medium text-slate-700 hover:text-slate-900">{t("public.contact")}</a>
            <LanguageSwitcher />
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url('${company.cover_url ? `${BACKEND}${company.cover_url}` : "https://images.pexels.com/photos/262353/pexels-photo-262353.jpeg"}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-slate-900/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32 text-white">
          <div className="flex items-start gap-6">
            {company.logo_url && (
              <img src={`${BACKEND}${company.logo_url}`} alt={company.name} className="w-20 h-20 rounded-lg object-cover border-2 border-white/30" />
            )}
            <div className="flex-1">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-white/80 mb-2">{company.industry || "Export Company"}</div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">{company.name}</h1>
              <p className="mt-4 text-lg text-slate-100 max-w-3xl leading-relaxed">{company.description}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href="#contact" data-testid="hero-cta-inquiry" className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-slate-900 rounded-md font-semibold text-sm">{t("public.cta_inquiry")}</a>
                <a href="#products" className="inline-flex items-center gap-2 px-5 py-2.5 border border-white/40 text-white rounded-md font-semibold text-sm">{t("public.products")}</a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About + Info Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16 lg:py-24 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12">
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0052CC] mb-3">{t("public.about")}</div>
          <h2 className="font-heading text-3xl font-semibold text-slate-900 tracking-tight">{company.name}</h2>
          {company.export_experience && <p className="mt-4 text-slate-600 leading-relaxed">Export experience: {company.export_experience}</p>}
          {company.description && <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-line">{company.description}</p>}

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {company.countries_served?.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3"><Globe2 className="w-4 h-4" /> {t("public.countries_served")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {company.countries_served.map((c) => <span key={c} className="px-2.5 py-1 bg-slate-100 rounded text-xs font-medium text-slate-700">{c}</span>)}
                </div>
              </div>
            )}
            {company.certifications?.length > 0 && (
              <div className="border border-slate-200 rounded-lg p-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3"><Award className="w-4 h-4" /> {t("public.certifications")}</div>
                <div className="flex flex-wrap gap-1.5">
                  {company.certifications.map((c) => <span key={c} className="px-2.5 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-semibold">{c}</span>)}
                </div>
              </div>
            )}
          </div>
        </div>
        <aside className="border border-slate-200 rounded-lg p-6 self-start" data-testid="public-contact-card">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-4">{t("public.contact")}</div>
          <div className="space-y-3 text-sm">
            {company.contact_email && <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-slate-400" /> {company.contact_email}</div>}
            {company.contact_phone && <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-slate-400" /> {company.contact_phone}</div>}
            {company.website && <div className="flex items-center gap-2 text-slate-700"><Globe2 className="w-4 h-4 text-slate-400" /> {company.website}</div>}
            {company.address && <div className="flex items-center gap-2 text-slate-700"><MapPin className="w-4 h-4 text-slate-400" /> {company.address}</div>}
          </div>
        </aside>
      </section>

      {/* Products */}
      {products.length > 0 && (
        <section id="products" className="bg-[#F8FAFC] py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="flex items-end justify-between mb-12 flex-wrap gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0052CC] mb-2">{t("public.products")}</div>
                <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight">Product Catalogue</h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="public-products-grid">
              {products.map((p) => (
                <div key={p.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden hover:-translate-y-1 hover:shadow-sm transition-all">
                  <div className="aspect-[4/3] bg-slate-100">
                    {p.images?.[0] ? (
                      <img src={`${BACKEND}${p.images[0]}`} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">No image</div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{p.category || "—"}</div>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mt-1">{p.name}</h3>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.description}</p>
                    {p.moq && <div className="mt-3 text-xs text-slate-500">MOQ: <span className="font-semibold text-slate-700">{p.moq}</span></div>}
                    {p.certifications?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {p.certifications.map((c) => <span key={c} className="text-[10px] px-2 py-0.5 bg-emerald-50 text-emerald-700 rounded font-semibold">{c}</span>)}
                      </div>
                    )}
                    <button onClick={() => { setForm((f) => ({ ...f, product_id: p.id, inquiry_type: "quotation" })); document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" }); }} className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-[#0052CC] hover:underline" data-testid={`product-inquiry-${p.id}`}>
                      {t("public.cta_quote")} →
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sales team */}
      {sales_reps.length > 0 && (
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#0052CC] mb-2">{t("public.sales_team")}</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold text-slate-900 tracking-tight mb-12">Our representatives</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sales_reps.map((r) => (
                <div key={r.id} className="border border-slate-200 rounded-lg p-6 flex gap-4">
                  <div className="w-16 h-16 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
                    {r.photo_url ? <img src={`${BACKEND}${r.photo_url}`} alt={r.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400 font-semibold">{r.name[0]}</div>}
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-slate-900">{r.name}</h3>
                    <div className="text-sm text-slate-600">{r.country}</div>
                    {r.languages_spoken?.length > 0 && <div className="mt-1 text-xs text-slate-500">{r.languages_spoken.join(" · ")}</div>}
                    {r.industries?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {r.industries.slice(0, 3).map((i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#0052CC]/10 text-[#0052CC] rounded">{i}</span>)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact form */}
      <section id="contact" className="bg-slate-900 text-white py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-white/60 mb-3">{t("public.contact")}</div>
            <h2 className="font-heading text-3xl lg:text-4xl font-semibold tracking-tight">{lang === "el" ? "Επικοινωνήστε μαζί μας" : "Get in touch"}</h2>
            <p className="mt-4 text-slate-300 max-w-md leading-relaxed">{lang === "el" ? "Στείλτε αίτημα ή ζητήστε προσφορά — η ομάδα μας θα απαντήσει σύντομα." : "Send an inquiry or request a quotation — our team will respond shortly."}</p>
          </div>
          <div>
            {sent ? (
              <div data-testid="inquiry-sent" className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-6 flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-400 mt-0.5" />
                <div>
                  <div className="font-semibold">{t("public.thanks")}</div>
                  <button className="text-sm text-slate-300 hover:text-white mt-2" onClick={() => setSent(false)}>Send another</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-3" data-testid="public-inquiry-form">
                <Field label={t("common.name")} testid="inquiry-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
                <Field label={t("common.email")} testid="inquiry-email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} required />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label={t("common.phone")} testid="inquiry-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} />
                  <Field label={t("common.country")} testid="inquiry-country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} />
                </div>
                <Field label="Company" testid="inquiry-company-name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} />
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">Type</label>
                  <select data-testid="inquiry-type" value={form.inquiry_type} onChange={(e) => setForm({ ...form, inquiry_type: e.target.value })} className="mt-1.5 w-full px-3 py-2.5 bg-white/5 border border-white/20 rounded-md text-sm text-white">
                    <option value="inquiry">{t("public.cta_inquiry")}</option>
                    <option value="quotation">{t("public.cta_quote")}</option>
                    <option value="meeting">{t("public.cta_meeting")}</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">{t("public.message")}</label>
                  <textarea data-testid="inquiry-message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 w-full px-3 py-2.5 bg-white/5 border border-white/20 rounded-md text-sm text-white min-h-[120px]" />
                </div>
                <button data-testid="inquiry-submit" type="submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-white text-slate-900 rounded-md font-semibold text-sm">
                  <Send className="w-4 h-4" /> {t("public.send")}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <footer className="bg-slate-950 text-slate-400 py-8 text-sm text-center">
        © 2026 {company.name} · Powered by {t("app.name")}
      </footer>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-white/70">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2.5 bg-white/5 border border-white/20 rounded-md text-sm text-white placeholder-white/40 focus:outline-none focus:border-white/50" />
    </div>
  );
}
